// @ts-check
import { Crosshair, RotateCcw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * @typedef {{ x: number, y: number }} FocalPoint percentages, 0-100
 * @typedef {{ label: string, ratio: string }} FocalPreview ratio is a CSS aspect-ratio value, e.g. "16 / 9"
 */

const CENTER = /** @type {FocalPoint} */ ({ x: 50, y: 50 });
const clamp = (/** @type {number} */ n) => Math.min(100, Math.max(0, n));

/** Build a focal point from two API columns; null unless both are numbers. */
export const toFocal = (/** @type {unknown} */ x, /** @type {unknown} */ y) =>
  typeof x === 'number' && typeof y === 'number' ? /** @type {FocalPoint} */ ({ x, y }) : null;

/** CSS `object-position` for a focal point; center when unset. */
export const focalToObjectPosition = (/** @type {FocalPoint | null | undefined} */ focal) =>
  focal ? `${focal.x}% ${focal.y}%` : '50% 50%';

/** @type {FocalPreview[]} */
const DEFAULT_PREVIEWS = [
  { label: 'Landscape 16:9', ratio: '16 / 9' },
  { label: 'Portrait 4:5', ratio: '4 / 5' },
];

/**
 * Modal for choosing the point of an image that must stay visible when the storefront
 * crops it with `object-cover`. Click or drag on the image to place the marker; the
 * previews on the right show the crop at each aspect ratio the image is used in.
 *
 * Non-destructive: only two integers are saved, the original image is untouched.
 */
const FocalPointPicker = ({
  src,
  value = /** @type {FocalPoint | null} */ (null),
  onSave,
  onClose,
  previews = DEFAULT_PREVIEWS,
  title = 'Set focal point',
}) => {
  const [focal, setFocal] = useState(value || CENTER);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(/** @type {HTMLImageElement | null} */ (null));
  const dragging = useRef(false);

  useEffect(() => {
    const handleKey = (/** @type {KeyboardEvent} */ e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // The <img> is sized to its content (no object-fit letterboxing), so its bounding box
  // IS the picture, so pointer offset / box size gives the percentage directly.
  const setFromPointer = useCallback((/** @type {{ clientX: number, clientY: number }} */ e) => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    setFocal({
      x: Math.round(clamp(((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.round(clamp(((e.clientY - rect.top) / rect.height) * 100)),
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(focal);
      onClose();
    } catch {
      // Caller surfaces the error (toast); keep the modal open so the pick isn't lost.
    } finally {
      setSaving(false);
    }
  };

  const position = focalToObjectPosition(focal);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div>
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="text-xs text-muted-foreground">
              Click or drag to mark the part of the image that must stay visible.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-accent" title="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4">
          {/* Picker */}
          <div className="flex items-center justify-center bg-muted rounded-lg p-2 min-h-[240px]">
            <div
              className="relative inline-block cursor-crosshair select-none touch-none"
              onPointerDown={(e) => {
                dragging.current = true;
                e.currentTarget.setPointerCapture(e.pointerId);
                setFromPointer(e);
              }}
              onPointerMove={(e) => dragging.current && setFromPointer(e)}
              onPointerUp={(e) => {
                dragging.current = false;
                e.currentTarget.releasePointerCapture(e.pointerId);
              }}
              onPointerCancel={() => {
                dragging.current = false;
              }}
            >
              <img
                ref={imgRef}
                src={src}
                alt=""
                draggable={false}
                className="block max-w-full max-h-[55vh] w-auto h-auto pointer-events-none"
              />
              {/* Crosshair guides */}
              <div
                className="absolute inset-y-0 w-px bg-white/70 pointer-events-none"
                style={{ left: `${focal.x}%` }}
              />
              <div className="absolute inset-x-0 h-px bg-white/70 pointer-events-none" style={{ top: `${focal.y}%` }} />
              {/* Marker */}
              <div
                className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-black/40 shadow-[0_0_0_1px_rgba(0,0,0,0.5)] pointer-events-none"
                style={{ left: `${focal.x}%`, top: `${focal.y}%` }}
              />
            </div>
          </div>

          {/* Crop previews */}
          <div className="space-y-3">
            <p className="text-xs font-medium text-muted-foreground">How it will crop</p>
            {previews.map((p) => (
              <div key={p.label}>
                <div className="w-full overflow-hidden rounded-lg bg-accent" style={{ aspectRatio: p.ratio }}>
                  <img src={src} alt="" className="w-full h-full object-cover" style={{ objectPosition: position }} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">{p.label}</p>
              </div>
            ))}
            <p className="text-[11px] text-muted-foreground/70 tabular-nums">
              x {focal.x}% · y {focal.y}%
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => setFocal(CENTER)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <RotateCcw size={14} />
            Reset to center
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm border border-input rounded-lg hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save focal point'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Hover-reveal icon button that opens the picker. Drop it inside a `relative group`
 * thumbnail next to ImagePreviewButton (which sits top-left; this one sits bottom-left).
 * `onSave(focal)` should persist and may throw; the picker stays open on failure.
 */
export const FocalPointButton = ({
  src,
  value = /** @type {FocalPoint | null} */ (null),
  onSave,
  previews = DEFAULT_PREVIEWS,
  title = 'Set focal point',
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  if (!src) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        title={value ? `Focal point ${value.x}% / ${value.y}% — click to change` : title}
        className={
          className ||
          `absolute bottom-2 left-2 p-1.5 rounded-full transition-colors opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 z-10 ${
            value ? 'bg-card text-foreground hover:bg-accent' : 'bg-black/60 hover:bg-black/80 text-white'
          }`
        }
      >
        <Crosshair size={14} />
      </button>
      {open && (
        <FocalPointPicker
          src={src}
          value={value}
          onSave={onSave}
          onClose={() => setOpen(false)}
          previews={previews}
          title={title}
        />
      )}
    </>
  );
};

export default FocalPointPicker;
